import { useState } from 'react';
import {
  FiAlertTriangle,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiMinus,
  FiPlus
} from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import {
  lotProgress,
  productProgress,
  type CountState,
  type ZoneCountLot,
  type ZoneCountProduct
} from '../zoneCountSteps';

type ZoneCountProductCardProps = {
  product: ZoneCountProduct;
  count: CountState;
  /** Alternates on every counted scan of this product, so the flash animation restarts each time. */
  flash?: 'a' | 'b' | null;
  onSetBulk: (lotKey: string, value: number | null) => void;
};

export const lotLine = (lot: { lot?: string | null; expiration?: string | null }) =>
  `Lot ${lot.lot || '—'} · Exp ${formatDateOnly(lot.expiration) || '—'}`;

/**
 * The count of one bulk lot: a number pad entry with − / + and a one-tap "matches the record".
 * Empty means "not counted yet", which is different from 0 (counted, none there).
 */
function BulkCountInput({
  lot,
  value,
  productName,
  onChange
}: {
  lot: ZoneCountLot;
  value: number | null;
  productName: string;
  onChange: (value: number | null) => void;
}) {
  const label = `Units counted of ${productName}, ${lotLine(lot)}`;
  return (
    <div className="zc-bulk">
      <div className="zc-bulk__stepper">
        <button
          type="button"
          aria-label="One less"
          disabled={value == null || value <= 0}
          onClick={() => onChange(Math.max(0, (value ?? 0) - 1))}
        >
          <FiMinus />
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          placeholder="Count"
          aria-label={label}
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') onChange(null);
            else {
              const n = parseInt(raw, 10);
              onChange(Number.isFinite(n) ? n : null);
            }
          }}
        />
        <button type="button" aria-label="One more" onClick={() => onChange((value ?? 0) + 1)}>
          <FiPlus />
        </button>
      </div>
      {value !== lot.bulkQuantity && (
        <button
          type="button"
          className="zc-bulk__match"
          onClick={() => onChange(lot.bulkQuantity)}
        >
          All {lot.bulkQuantity} here
        </button>
      )}
    </div>
  );
}

function LotRow({
  lot,
  count,
  productName,
  onSetBulk
}: {
  lot: ZoneCountLot;
  count: CountState;
  productName: string;
  onSetBulk: (lotKey: string, value: number | null) => void;
}) {
  const progress = lotProgress(lot, count);
  const short = progress.bulkCounted != null && progress.bulkCounted < lot.bulkQuantity;
  return (
    <div
      className={[
        'zc-lot',
        progress.done && 'zc-lot--done',
        (progress.over > 0 || short) && 'zc-lot--variance'
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="zc-lot__head">
        <span className="zc-lot__id">{lotLine(lot)}</span>
        <span className="zc-lot__count" aria-label={`${progress.counted} of ${lot.expected} counted`}>
          {progress.done && <FiCheck aria-hidden />}
          {progress.counted}/{lot.expected}
        </span>
      </div>
      {lot.units.length > 0 && (
        <div className="zc-lot__meta">
          {progress.scannedUnits} of {lot.units.length} serialized scanned
        </div>
      )}
      {lot.bulkQuantity > 0 && (
        <>
          {lot.units.length > 0 && (
            <div className="zc-lot__meta">
              Plus {lot.bulkQuantity} without serial records — enter how many are here
            </div>
          )}
          <BulkCountInput
            lot={lot}
            value={progress.bulkCounted}
            productName={productName}
            onChange={(value) => onSetBulk(lot.key, value)}
          />
          {progress.over > 0 && (
            <div className="zc-lot__flag">{progress.over} over the record</div>
          )}
          {short && (
            <div className="zc-lot__flag">
              {lot.bulkQuantity - (progress.bulkCounted ?? 0)} short of the record
            </div>
          )}
        </>
      )}
      {lot.drift && (
        <div className="zc-lot__flag zc-lot__flag--drift">
          <FiAlertTriangle aria-hidden /> Records disagree — count only, can’t adjust
        </div>
      )}
    </div>
  );
}

/**
 * One product on the count, as the Verify screen lists it: the product, `counted / expected`, and
 * its lots — serialized units scanned against the units on record, bulk lots counted by number.
 * Green once every lot matches, red while something is over or short of the record. The serials
 * still to find can be opened under the card; they are listed, never tickable — a unit is counted
 * by scanning it.
 */
function ZoneCountProductCard({ product, count, flash = null, onSetBulk }: ZoneCountProductCardProps) {
  const [showSerials, setShowSerials] = useState(false);
  const progress = productProgress(product, count);
  const scanned = new Set(count.scanned);
  const serialLots = product.lots.filter((lot) => lot.units.length > 0);
  const remainingSerials = serialLots.flatMap((lot) =>
    lot.units.filter((u) => !scanned.has(u.serializedItemId)).map((u) => ({ lot, unit: u }))
  );
  const lastSerial = [...count.scanned]
    .reverse()
    .map((id) => serialLots.flatMap((l) => l.units).find((u) => u.serializedItemId === id))
    .find(Boolean);
  const anyVariance = product.lots.some((lot) => {
    const p = lotProgress(lot, count);
    return p.over > 0 || (p.bulkCounted != null && p.bulkCounted < lot.bulkQuantity);
  });
  const displayProduct = {
    title: product.productName,
    fdaProduct:
      product.ndc || product.deaSchedule
        ? { elevenDigitNdc: product.ndc, deaSchedule: product.deaSchedule }
        : null,
    upcProduct: product.upc ? { upc: product.upc } : null
  };

  return (
    <div
      className={[
        'zc-product',
        progress.done && 'zc-product--done',
        anyVariance && 'zc-product--variance',
        flash && `zc-product--flash-${flash}`
      ]
        .filter(Boolean)
        .join(' ')}
      data-key={product.key}
    >
      <div className="zc-product__head">
        <div className="zc-product__info">
          <WarehouseProductDisplay product={displayProduct}>
            {product.kind === 'SERIALIZED' && serialLots.length > 0 && (
              <span className="zc-product__how">Scan each unit</span>
            )}
            {serialLots.length === 0 && <span className="zc-product__how">Enter the count</span>}
            {!!lastSerial && (
              <span className="zc-product__serial">Last serial {lastSerial.serialNumber}</span>
            )}
          </WarehouseProductDisplay>
        </div>
        <div
          className="zc-product__count"
          aria-label={`${progress.counted} of ${product.expected} counted`}
        >
          {progress.done && <FiCheck aria-hidden />}
          <span>
            {progress.counted}/{product.expected}
          </span>
        </div>
      </div>

      <div className="zc-product__lots">
        {product.lots.map((lot) => (
          <LotRow
            key={lot.key}
            lot={lot}
            count={count}
            productName={product.productName}
            onSetBulk={onSetBulk}
          />
        ))}
      </div>

      {remainingSerials.length > 0 && (
        <div className="zc-product__serials">
          <button
            type="button"
            className="zc-product__serials-toggle"
            aria-expanded={showSerials}
            onClick={() => setShowSerials((open) => !open)}
          >
            {showSerials ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
            {remainingSerials.length} serial{remainingSerials.length === 1 ? '' : 's'} not scanned
            yet
          </button>
          {showSerials && (
            <ul className="zc-product__serial-list">
              {remainingSerials.map(({ lot, unit }) => (
                <li key={unit.serializedItemId}>
                  <span className="zc-mono">{unit.serialNumber}</span>
                  {serialLots.length > 1 && <small>Lot {lot.lot || '—'}</small>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ZoneCountProductCard;
